import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Brain, Calendar, BarChart2, Link, Mail, Video,
  Shield, TrendingUp, BookOpen, Layout, ChevronLeft,
  ChevronRight, Settings, Zap, FlaskConical, Lightbulb, Film,
  Building2, UserCheck, PieChart, Repeat2, Sparkles, Flame, ClipboardCheck, Clock, Globe, LogOut, Users, Send,
} from 'lucide-react';
import { useBrainstorm } from '../../context/BrainstormContext';
import { EinsteinPanel } from '../EinsteinPanel';
import { OnboardingWizard } from '../OnboardingWizard';

const SIDEBAR_SECTIONS = [
  {
    label: 'AI Agents',
    items: [
      { label: 'Home', icon: Home, path: '/' },
      { label: 'Einstein', icon: Brain, path: '/strategist', badge: 'AI' },
    ],
  },
  {
    label: 'Labs',
    items: [
      { label: 'Brainstorm Lab', icon: Lightbulb, path: '/brainstorm', badge: 'NEW' },
      { label: 'Research Lab', icon: FlaskConical, path: '/research', badge: 'NEW' },
    ],
  },
  {
    label: 'Strategy',
    items: [
      { label: 'Trending Topics', icon: Flame, path: '/trending', badge: 'NEW' },
      { label: 'Review Dashboard', icon: ClipboardCheck, path: '/review-dashboard' },
      { label: 'Auto Strategy', icon: Sparkles, path: '/auto-strategy' },
      { label: 'Content Studio', icon: Globe, path: '/content-studio', badge: 'NEW' },
      { label: 'Influencer Hub', icon: UserCheck, path: '/influencer' },
    ],
  },
  {
    label: 'Content',
    items: [
      { label: 'Blog Publisher', icon: BookOpen, path: '/blog' },
      { label: 'Content Calendar', icon: Calendar, path: '/calendar' },
      { label: 'Preview Board', icon: Layout, path: '/board' },
    ],
  },
  {
    label: 'Video',
    items: [
      { label: 'Social Videos', icon: Video, path: '/video' },
      { label: 'Ad Video Agent', icon: Film, path: '/advideo' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { label: 'Competitive Intel', icon: Shield, path: '/intel' },
      { label: 'Performance AI', icon: TrendingUp, path: '/performance' },
      { label: 'Analytics Board', icon: PieChart, path: '/dashboard' },
      { label: 'Content Plan', icon: Calendar, path: '/content-plan', badge: 'SUN+WED' },
      { label: 'Analytics', icon: TrendingUp, path: '/analytics' },
      { label: 'Publishing Hub', icon: Send, path: '/publishing' },
    ],
  },
  {
    label: 'Publishing',
    items: [
      { label: 'Post Scheduler', icon: Clock, path: '/scheduler' },
      { label: 'Brand Manager', icon: Building2, path: '/brands' },
      { label: 'Traffic Converter', icon: Link, path: '/converter' },
      { label: 'Newsletter', icon: Mail, path: '/newsletter' },
      { label: 'Subscribers', icon: Users, path: '/newsletter-subscribers' },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Settings', icon: Settings, path: '/settings' },
    ],
  },
];

export function AppLayout({ children }) {
  const [expanded, setExpanded] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const { activeSession } = useBrainstorm() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [authUser, setAuthUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ideaworks_user') || 'null'); } catch { return null; }
  });

  const handleLogout = () => {
    localStorage.removeItem('ideaworks_token');
    localStorage.removeItem('ideaworks_user');
    delete window.axios?.defaults?.headers?.common?.['Authorization'];
    window.location.href = '/';
  };

  // Auto-launch onboarding wizard on first visit
  useEffect(() => {
    const done = localStorage.getItem('ideaworks_onboarding_done');
    if (!done) {
      // Small delay so the app renders first
      const t = setTimeout(() => setShowWizard(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const sw = expanded ? 220 : 56;

  return (
    <div className="flex h-full overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Sidebar */}
      <div
        className="flex-shrink-0 flex flex-col overflow-hidden transition-all duration-200 ease-in-out"
        style={{
          width: sw,
          background: 'rgba(10,10,20,0.98)',
          borderRight: '1px solid var(--llmc-border)',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 p-3 min-h-[56px]">
          <div
            className="flex-shrink-0 w-8 h-8 rounded-[10px] flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #ff6eb4, #60a5fa)' }}
          >
            <Zap size={16} className="text-white" />
          </div>
          {expanded && (
            <div className="overflow-hidden">
              <div className="text-sm font-black tracking-tight whitespace-nowrap" style={{ letterSpacing: '-0.02em' }}>
                <span style={{ color: '#ff6eb4' }}>Idea</span><span style={{ color: '#60a5fa' }}>Works</span>
              </div>
              <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>v5.0.0 · Einstein AI</div>
            </div>
          )}
        </div>

        <div className="mx-3 mb-1" style={{ height: 1, background: 'var(--llmc-border)' }} />

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-1.5 py-1">
          {SIDEBAR_SECTIONS.map((section, si) => (
            <div key={section.label} className={si > 0 ? 'mt-3' : ''}>
              {expanded && (
                <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-widest"
                  style={{ color: 'rgba(255,255,255,0.2)' }}>
                  {section.label}
                </div>
              )}
              {section.items.map(({ label, icon: Icon, path, badge }) => {
                const active = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
                return (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    data-testid={`nav-${path.replace('/', '') || 'home'}`}
                    title={!expanded ? label : undefined}
                    className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-[10px] mb-0.5 text-left transition-all duration-150 group ${
                      active ? 'nav-active' : 'llmc-btn-ghost'
                    }`}
                  >
                    <Icon
                      size={16}
                      className="flex-shrink-0"
                      style={{ color: active ? '#a68eff' : 'rgba(255,255,255,0.4)' }}
                    />
                    {expanded && (
                      <span className="flex-1 flex items-center gap-1.5 text-[13px] font-medium whitespace-nowrap overflow-hidden"
                        style={{ color: active ? '#a68eff' : 'rgba(232,232,240,0.7)' }}>
                        {label}
                        {badge && (
                          <span className="text-[9px] font-bold px-1 py-0.5 rounded"
                            style={{ background: 'rgba(124,94,250,0.2)', color: '#a68eff' }}>
                            {badge}
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Live Brainstorm Feed */}
        {activeSession && activeSession.status === 'running' && (
          <div
            className="mx-2 mb-2 overflow-hidden rounded-xl cursor-pointer"
            style={{
              background: `${activeSession.modeColor || '#7c5efa'}10`,
              border: `1px solid ${activeSession.modeColor || '#7c5efa'}35`,
              animation: 'fade-in 0.4s ease',
            }}
            onClick={() => navigate('/brainstorm')}
            title="Click to view brainstorm"
          >
            {expanded ? (
              <div className="p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <div
                    className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0"
                    style={{ background: activeSession.modeColor || '#7c5efa' }}
                  />
                  <span className="text-[9px] font-black uppercase tracking-wider"
                    style={{ color: activeSession.modeColor || '#7c5efa' }}>
                    Live · {activeSession.mode}
                  </span>
                </div>
                <div className="text-[10px] font-medium text-white truncate mb-1.5">
                  {activeSession.idea?.slice(0, 38)}{activeSession.idea?.length > 38 ? '...' : ''}
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: activeSession.totalRounds || 4 }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        height: 3,
                        flex: 1,
                        borderRadius: 2,
                        background: i < (activeSession.currentRound || 0)
                          ? activeSession.modeColor || '#7c5efa'
                          : 'rgba(255,255,255,0.1)',
                        transition: 'background 0.4s ease',
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-2">
                <div
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ background: activeSession.modeColor || '#7c5efa' }}
                />
              </div>
            )}
          </div>
        )}

        {/* Collapse toggle + user info */}
        <div className="p-1.5 border-t" style={{ borderColor: 'var(--llmc-border)' }}>
          {expanded && authUser && (
            <div className="flex items-center justify-between px-2 py-1.5 mb-1 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-white truncate">{authUser.name || authUser.email}</div>
                <div className="text-[9px]" style={{ color: authUser.role === 'admin' ? '#3dffa0' : 'rgba(255,255,255,0.3)' }}>
                  {authUser.role === 'admin' ? 'Admin' : 'User'}
                </div>
              </div>
              <button onClick={handleLogout}
                className="flex-shrink-0 p-1.5 rounded-lg hover:opacity-70 transition-all"
                style={{ color: 'rgba(255,255,255,0.3)' }}
                title="Sign out">
                <LogOut size={12} />
              </button>
            </div>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center p-2 rounded-[10px] llmc-btn-ghost"
            data-testid="sidebar-toggle"
          >
            {expanded
              ? <ChevronLeft size={16} style={{ color: 'rgba(255,255,255,0.4)' }} />
              : <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.4)' }} />}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {children}
      </div>

      {/* Global Einstein floating panel */}
      <EinsteinPanel />

      {/* Onboarding wizard (auto on first visit) */}
      {showWizard && (
        <OnboardingWizard onComplete={() => setShowWizard(false)} />
      )}
    </div>
  );
}
